import firebase from 'firebase/app'
import React, { useContext, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import SearchIcon from '@material-ui/icons/Search'
import { FirestoreContext } from '../../providers/firestore-provider'
import { CustomDialogContent, FindSuggestions } from '..'
import Typography from '@material-ui/core/Typography'
import {
  capitalize,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  TextField,
  Button,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core'
import buildInfo from '../../../package.json'
import { useDispatch } from 'react-redux'
import { openSnackbar } from '../../ducks/snackbar/actions'
import { useDialog } from '../../providers/dialog-provider'
import {
  ICircle,
  addDoseToCircle,
  addDoseToSeveralCircles,
  addToSuggestedDoses,
} from 'daily-dose-common'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    suggestionsButton: {
      margin: theme.spacing(1),
    },
  })
)

interface IAddDoseProps {
  doseRef?: firebase.firestore.CollectionReference<firebase.firestore.DocumentData>
  onAddDose: () => void
  circle?: ICircle
  onCancelDose: () => void
  toAllCircles?: boolean
}

type InputsForm = {
  name: string
  description: string
}

const AddDoseForm = ({
  onAddDose,
  circle,
  onCancelDose,
  toAllCircles = false,
}: IAddDoseProps) => {
  const classes = useStyles()
  const { currentUser, myCircles } = useContext(FirestoreContext)
  const dispatch = useDispatch()
  const { handleSubmit, errors, control } = useForm<InputsForm>()
  const { openDialog, closeDialog } = useDialog()

  const circlesInitialState = toAllCircles
    ? myCircles?.map((circle) => ({
        ...circle,
      }))
    : []

  const [selectedCircles, setSelectedCircles] = useState(() => circlesInitialState)
  const [isAddToSuggestedDoses, setIsAddToSuggestedDoses] = useState(true)

  const onSubmit = async (data: InputsForm) => {
    const { name, description } = data

    // TODO: Refactor
    try {
      // For admin users
      let suggestedDoseId
      if (currentUser?.admin && isAddToSuggestedDoses)
        suggestedDoseId = (await addToSuggestedDoses(name, description, currentUser))
          .id

      if (toAllCircles && currentUser) {
        await addDoseToSeveralCircles(
          selectedCircles,
          currentUser,
          name,
          description,
          buildInfo.version,
          suggestedDoseId
        )

        dispatch(
          openSnackbar(
            `Awesome! You just added a dose to the ${circle?.name} group`,
            'success'
          )
        )
        onAddDose()
      } else {
        if (circle && currentUser) {
          await addDoseToCircle(
            circle,
            currentUser,
            name,
            description,
            buildInfo.version,
            suggestedDoseId
          )

          dispatch(
            openSnackbar(
              `Awesome! You just added a dose to the ${circle?.name} group`,
              'success'
            )
          )
          onAddDose()
        }
      }
    } catch (error) {
      console.warn('Error adding a dose', error)
      dispatch(
        openSnackbar(
          'Opps. We could not add your dose(s). Make sure you have added both title and content.',
          'error'
        )
      )
    }
  }

  const isCircleChecked = (circle: ICircle) =>
    selectedCircles.some((c) => c.id === circle.id)

  const onToggleCheckbox = (circle: ICircle) => {
    const shouldRemove = selectedCircles.some((c) => c.id === circle.id)

    if (shouldRemove) {
      if (selectedCircles.length === 1) {
        return dispatch(
          openSnackbar(
            `You need to have at least one group checked to send the dose`,
            'info'
          )
        )
      }
      setSelectedCircles((selectedCircles) =>
        selectedCircles.filter((c) => c.id !== circle.id)
      )
    } else {
      setSelectedCircles((selectedCircles) => selectedCircles.concat(circle))
    }
  }

  const onOpenSuggestions = () => {
    // Call addSuggestedDose

    openDialog({
      color: 'primary',
      title: 'Find suggestions',
      confirmText: '',
      custom: true,
      description: <FindSuggestions onCancel={() => closeDialog()} />,
      fullScreen: true,
    })
  }

  // TODO: Refactor this. Create a custom Dialog component
  return (
    <CustomDialogContent
      title="Add Dose"
      confirmText="Add"
      onCancel={onCancelDose}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Typography variant="h6" color="textPrimary" gutterBottom>
        {!toAllCircles && `${capitalize(circle?.name || '')}`}
      </Typography>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="AddDose"
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Controller
              as={TextField}
              control={control}
              rules={{ required: true }}
              defaultValue=""
              variant="outlined"
              label="Name"
              margin="normal"
              fullWidth
              autoComplete="off"
              type="text"
              error={!!errors.name}
              helperText={!!errors.name && 'Your dose name is missing.'}
              name="name"
              placeholder="The catchy title goes here"
              InputLabelProps={{
                shrink: true,
              }}
            />

            <Controller
              as={TextField}
              label="Description"
              multiline
              control={control}
              rules={{ required: true }}
              defaultValue=""
              variant="outlined"
              margin="normal"
              fullWidth
              autoComplete="off"
              type="text"
              name="description"
              placeholder="Describe your dose"
              rows={4}
              InputLabelProps={{
                shrink: true,
              }}
              error={!!errors.description}
              helperText={
                !!errors.description && 'Your dose description is missing.'
              }
            />
          </Grid>
          {currentUser?.admin && (
            <Grid item xs>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAddToSuggestedDoses}
                    onChange={() => setIsAddToSuggestedDoses(!isAddToSuggestedDoses)}
                    name="Add to suggested dose"
                    color="secondary"
                  />
                }
                label="Add to suggested doses"
              />
            </Grid>
          )}
          <Grid item xs>
            {toAllCircles && (
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography variant="subtitle1">Send to groups:</Typography>
                </FormLabel>
                <FormGroup>
                  {myCircles?.map((circle) => (
                    <FormControlLabel
                      key={circle.id}
                      control={
                        <Checkbox
                          checked={!!isCircleChecked(circle)}
                          onChange={() => onToggleCheckbox(circle)}
                          name={circle.name}
                          color="primary"
                        />
                      }
                      label={circle.name}
                    />
                  ))}
                </FormGroup>
                <FormHelperText>
                  One click away to enjoy the moment 💙
                </FormHelperText>
              </FormControl>
            )}
          </Grid>
          <Grid container item xs={12} justify="center">
            <Button
              style={{ textTransform: 'none' }}
              variant="contained"
              color="primary"
              className={classes.suggestionsButton}
              startIcon={<SearchIcon />}
              size="small"
              onClick={onOpenSuggestions}
            >
              Find suggestions
            </Button>
          </Grid>
        </Grid>
      </form>
    </CustomDialogContent>
  )
}

export default AddDoseForm
